import { useState, type FormEvent } from "react";
import { useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { QuoterModel } from "../types";
import { useSaveQuoterConfig } from "./quoterAdminData";
import {
  EMPTY_MODEL_FORM,
  MODEL_FIELDS,
  modelToFormValues,
  toModelData,
  validateModelForm,
  type ModelFormValues,
} from "./quoterConfigRules";
import type { QuoterProjectData } from "./quoterEngine";
import { formatMxn } from "./quoterFormat";
import { QuoterField } from "./QuoterField";

const TEXT_FIELDS = ["code", "name", "program", "garage"];

const ModelDialog = ({
  model,
  onClose,
  onSave,
}: {
  model: QuoterModel | null;
  onClose: () => void;
  onSave: (code: string, data: ReturnType<typeof toModelData>) => Promise<void>;
}) => {
  const translate = useTranslate();
  const [values, setValues] = useState<ModelFormValues>(
    model ? modelToFormValues(model) : EMPTY_MODEL_FORM,
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const errors = validateModelForm(values, model == null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      await onSave(values.code.trim().toUpperCase(), toModelData(values));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate(
              model
                ? "crm.quoter.models.edit_title"
                : "crm.quoter.models.new_title",
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          {MODEL_FIELDS.map((field) => (
            <QuoterField
              key={field}
              id={`quoter-model-${field}`}
              label={translate(`crm.quoter.models.fields.${field}`)}
              value={values[field]}
              numeric={!TEXT_FIELDS.includes(field)}
              // The code identifies the model: it cannot change once created.
              disabled={field === "code" && model != null}
              error={submitted ? errors[field] : null}
              onChange={(value) =>
                setValues((current) => ({ ...current, [field]: value }))
              }
            />
          ))}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={onClose}
            >
              {translate("crm.quoter.config.cancel")}
            </Button>
            <Button type="submit" className="h-11 sm:h-9" disabled={saving}>
              {translate("crm.quoter.config.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Almikia Epic EQ2 (TASK-099): the models (typologies) of a project with
 * their base and roof prices and descriptive data. A model is archived,
 * never deleted, and its code cannot change once created.
 */
export const QuoterModelsEditor = ({
  project,
}: {
  project: QuoterProjectData;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const save = useSaveQuoterConfig();
  // undefined = closed, null = creating, a model = editing it.
  const [editing, setEditing] = useState<QuoterModel | null | undefined>(
    undefined,
  );
  const models = [...project.models].sort(
    (a, b) => a.sort_order - b.sort_order,
  );

  const persist = async (
    model: QuoterModel | null,
    data: Record<string, unknown>,
  ) => {
    try {
      await save("quoter_models", model?.id ?? null, data);
      notify("crm.quoter.config.saved");
      setEditing(undefined);
    } catch {
      notify("crm.quoter.config.save_error", { type: "error" });
    }
  };

  return (
    <section
      className="space-y-3"
      aria-label={translate("crm.quoter.models.title")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {translate("crm.quoter.models.title")}
        </h2>
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          onClick={() => setEditing(null)}
        >
          {translate("crm.quoter.models.new")}
        </Button>
      </div>
      <ul className="space-y-2" data-testid="model-list">
        {models.map((model) => (
          <li key={model.id}>
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{model.name}</span>
                  <span className="text-base tabular-nums sm:text-sm">
                    {formatMxn(model.base_price)}
                    {model.archived_at
                      ? ` · ${translate("crm.quoter.config.archived")}`
                      : ""}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() => setEditing(model)}
                  >
                    {translate("crm.quoter.config.edit")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    onClick={() =>
                      persist(model, {
                        archived_at: model.archived_at
                          ? null
                          : new Date().toISOString(),
                      })
                    }
                  >
                    {translate(
                      model.archived_at
                        ? "crm.quoter.config.reactivate"
                        : "crm.quoter.config.archive",
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      {editing !== undefined ? (
        <ModelDialog
          model={editing}
          onClose={() => setEditing(undefined)}
          onSave={(code, data) =>
            persist(
              editing,
              editing
                ? data
                : {
                    ...data,
                    code,
                    project_id: project.config.project_id,
                    sort_order:
                      Math.max(-1, ...models.map((row) => row.sort_order)) + 1,
                  },
            )
          }
        />
      ) : null}
    </section>
  );
};
